import { useLazyQuery } from '@apollo/client';
import { useHubble } from '@seek/hubble';
import { get as localStorageGet } from 'local-storage';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';

import {
  SearchLocationsSuggestDocument,
  type LocationSuggestion,
  type LocationSuggestionGroup,
  type LocationSuggestionType,
} from 'src/graphql/graphql';
import { useSelector } from 'src/store/react';
import { selectFeatureFlag, selectRecentSearches } from 'src/store/selectors';
import { useMelwaysCountry, useMelwaysLocale } from 'src/utils/melwaysHelper';

export interface FormattedPersonalisedSuggestion {
  label: string;
  suggestions: Array<
    LocationSuggestion & {
      value: {
        analyticsType: string;
      };
    }
  >;
}

export const DEBOUNCED_TIMEOUT = 100;

const formatSuggestionGroupsFromQuery = (
  locationSuggestions: LocationSuggestionGroup[],
): FormattedPersonalisedSuggestion[] => {
  const withSuggestions = locationSuggestions.filter(
    (ls) => ls.suggestions.length > 0,
  );
  return withSuggestions.map((locationSuggestion) => ({
    label: locationSuggestion.label,
    suggestions: locationSuggestion.suggestions.map(
      (suggestion: LocationSuggestion) => ({
        ...suggestion,
        value: {
          analyticsType: locationSuggestion.label.toLowerCase(),
        },
      }),
    ),
  }));
};

const isPersonalisedSuggestionGroup = (
  suggestionVal: LocationSuggestionType,
  locationQuery: string,
): suggestionVal is LocationSuggestionGroup =>
  suggestionVal?.__typename === 'LocationSuggestionGroup' &&
  locationQuery.trim() === '';

const getLastSearch = () => localStorageGet<string>('lastSearchWhere');

const useLocationSuggestions = () => {
  const locale = useMelwaysLocale();
  const country = useMelwaysCountry();
  const searches = useSelector(selectRecentSearches);
  const hubble = useHubble();
  const visitorId = hubble.visitorId();
  const remoteSearchFilterEnabled = useSelector(
    selectFeatureFlag('remoteSearchFilter'),
  );

  const [location, setLocation] = useState({ current: '', recent: '' });
  const [suggestions, setSuggestions] = useState<
    FormattedPersonalisedSuggestion[] | LocationSuggestionType[]
  >([]);
  const [personalisedSuggestionsCache, setPersonalisedSuggestionsCache] =
    useState<Record<string, FormattedPersonalisedSuggestion[]>>({});
  const [suggestionsCache, setSuggestionsCache] = useState<
    Record<string, LocationSuggestionType[]>
  >({});

  const [loadSuggestions] = useLazyQuery(SearchLocationsSuggestDocument, {
    fetchPolicy: 'no-cache',
    ssr: false,
    onCompleted: ({ searchLocationsSuggest }) => {
      const locationSuggestions = (searchLocationsSuggest?.suggestions ??
        []) as LocationSuggestionType[];

      const isPersonalised = isPersonalisedSuggestionGroup(
        locationSuggestions[0],
        location.current,
      );

      if (isPersonalised) {
        const formattedSuggestions = formatSuggestionGroupsFromQuery(
          locationSuggestions as LocationSuggestionGroup[],
        );
        setSuggestions(formattedSuggestions);

        setPersonalisedSuggestionsCache(
          (previousPersonalisedSuggestionsCache) => ({
            ...previousPersonalisedSuggestionsCache,
            [location.recent]: formattedSuggestions,
          }),
        );
      } else {
        setSuggestionsCache((previousSuggestionsCache) => ({
          ...previousSuggestionsCache,
          [location.current]: locationSuggestions,
        }));
        setSuggestions(locationSuggestions);
      }
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadWhereSuggestions = useCallback(
    debounce((args) => {
      loadSuggestions(args);
    }, DEBOUNCED_TIMEOUT),
    [loadSuggestions],
  );

  useEffect(
    () => () => {
      debouncedLoadWhereSuggestions.cancel();
    },
    [debouncedLoadWhereSuggestions],
  );

  const fetchWhereSuggestions = ({ query = '', recentSearchLocation = '' }) => {
    debouncedLoadWhereSuggestions({
      variables: {
        query,
        recentLocation: recentSearchLocation,
        count: 16,
        locale,
        country,
        visitorId,
        isRemoteEnabled: remoteSearchFilterEnabled,
      },
    });
  };

  const getRecentSearchWhere = useCallback(() => {
    const latestRecentSearch =
      searches && searches.length > 0 ? searches[0] : undefined;
    const recentWhereLocation = latestRecentSearch?.query?.where || '';

    return recentWhereLocation;
  }, [searches]);

  const getSuggestions = ({ text = '' }) => {
    const query = text.trim();
    const recentSearchLocation = getLastSearch() || getRecentSearchWhere();
    setLocation({ current: query, recent: recentSearchLocation });

    if (query === '') {
      if (!personalisedSuggestionsCache[recentSearchLocation]) {
        fetchWhereSuggestions({
          recentSearchLocation,
        });
      } else {
        setSuggestions(
          () => personalisedSuggestionsCache[recentSearchLocation],
        );
      }
    } else if (suggestionsCache[query]) {
      setSuggestions(suggestionsCache[query]);
    } else {
      fetchWhereSuggestions({
        query,
      });
    }
  };

  return {
    getSuggestions,
    suggestions,
  };
};

export default useLocationSuggestions;
